import { useEffect, useState } from "react";
import { Search, X, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import Portal from "@/components/Portal";

export default function InventoryPicker({ branch, onPick, onClose }) {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/inventory", { params: { branch_code: branch.code, q: q || undefined } });
        setRows(data.slice(0, 100));
      } catch (err) {
        toast.error(formatApiError(err, "Inventory load failed"));
      } finally {
        setLoading(false);
      }
    }, 220);
    return () => clearTimeout(t);
  }, [q, branch?.code]);

  return (
    <Portal>
      <div className="fixed inset-0 bg-[#2A2624]/55 z-50 flex items-center justify-center p-6" onClick={onClose}>
        <div className="bg-white w-full max-w-lg rounded-lg border border-sand p-5 max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="inv-picker">
          <div className="flex items-center justify-between mb-3">
            <div className="font-display text-lg text-forest">Pick from stock · {branch.code}</div>
            <button onClick={onClose} className="h-8 w-8 rounded-md border border-sand hover:bg-[#F9F6F0] flex items-center justify-center" data-testid="inv-picker-close"><X className="h-4 w-4" /></button>
          </div>
          <div className="relative mb-3">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5652]" />
            <input autoFocus data-testid="inv-picker-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or SKU…"
              className="w-full h-10 pl-10 pr-3 rounded-lg border border-sand focus:ring-2 focus:ring-[#1A4331]/40 focus:outline-none" />
          </div>
          <div className="flex-1 overflow-y-auto border border-sand rounded divide-y divide-[#E8E3D9]">
            {loading ? <div className="p-4 text-sm text-[#5C5652]">Loading…</div>
              : rows.length === 0 ? <div className="p-4 text-sm text-[#5C5652]">No items{q && ` matching "${q}"`}.</div>
              : rows.map(r => (
                <button
                  key={r.id}
                  onClick={() => onPick(r)}
                  disabled={r.stock_qty <= 0}
                  data-testid={`inv-pick-${r.sku}`}
                  className="w-full text-left px-3 py-2 hover:bg-[#F9F6F0] flex items-center gap-3 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-[#2A2624] truncate">{r.name}</div>
                    <div className="text-[10px] uppercase tracking-wider text-[#5C5652]">{r.sku} · per {r.unit}</div>
                  </div>
                  <div className="text-right">
                    <div className={`text-sm font-bold ${r.low_stock ? "text-[#C25946]" : "text-forest"}`}>
                      {r.stock_qty}
                      {r.low_stock && <AlertTriangle className="inline h-3 w-3 ml-1" strokeWidth={2} />}
                    </div>
                    <div className="text-xs font-mono text-forest">₹ {r.selling_price.toFixed(2)}</div>
                  </div>
                </button>
              ))}
          </div>
        </div>
      </div>
    </Portal>
  );
}
